// rules.jsx
import React, { useEffect, useMemo, useState } from 'react';
import '../fonts/breuer-headline.css';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { initQuestions } from '../../functions/setUserQuestions';
import { selectAdminToken } from '../../admin/sessionSlice';
import { processQuestions } from '../../admin/questionSlice';
import { setBackButtonUrl } from '../uiSlice';

function readPersistedUser() {
  try {
    return JSON.parse(sessionStorage.getItem('userData') || 'null');
  } catch {
    return null;
  }
}

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const { status, user } = useSelector((state) => state.auth);
  const [redirect, setRedirect] = useState('/welcome');
  const adminToken = useSelector(selectAdminToken);
  /** Quiz themes block on init; welcome/sort-and-toss themes are ready once themeData exists. */
  const [quizReady, setQuizReady] = useState(true);

  useEffect(() => {
    if (!themeData) return;

    // Welcome / full-house themes have no quiz config (no noOfQuestion) — skip setUserQuestions.
    const baseQuestions = parseInt(
      themeData.noOfQuestion ?? themeData.no_of_questions,
      10
    );
    const needsQuizInit = Number.isFinite(baseQuestions) && baseQuestions >= 1;

    if (!needsQuizInit) {
      setQuizReady(true);
      return;
    }

    const effectiveUser = user || readPersistedUser();
    if (!effectiveUser || !adminToken) {
      // Still show rules; quiz init can retry when auth is available.
      setQuizReady(true);
      return;
    }

    let cancelled = false;
    setQuizReady(false);

    dispatch(processQuestions())
      .unwrap()
      .then(() =>
        initQuestions({
          userId: effectiveUser.userId,
          email: effectiveUser.email,
          fullName: effectiveUser.name,
          themeName: themeData.themename,
          token: adminToken,
        })
      )
      .then((data) => {
        console.log('Init response:', data);
      })
      .catch((err) => {
        console.error('Error in process→init chain:', err);
      })
      .finally(() => {
        if (!cancelled) setQuizReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [themeData, user, adminToken, dispatch]);

  useEffect(() => {
    if (!themeData) return;
    setRedirect('/welcome');
  }, [themeData]);

  useEffect(() => {
    dispatch(setBackButtonUrl('/login?&save=true'));
  }, [dispatch, status, user]);

  const handleNextClick = (e) => {
    e.preventDefault();
    navigate(redirect);
  };

  const rulesTextColor = useMemo(() => {
    const raw =
      themeData?.landing_page_title_color ??
      themeData?.landing_page_title_colour;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  const nextButtonTextColor = useMemo(() => {
    const raw = themeData?.button_Textcolor ?? themeData?.button_text_color;
    if (raw == null || raw === '') return undefined;
    const c = String(raw).trim();
    return c || undefined;
  }, [themeData]);

  if (!themeData || !quizReady) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  return (
    <>
      <div className="user-rules-page">
        <div className="user-rules-stack">
          <fieldset
            className="user-rules-fieldset"
            style={rulesTextColor ? { color: rulesTextColor } : undefined}
          >
            <legend className="user-rules-heading">How to Play</legend>
            <ul className="user-rules-list">
              {(themeData?.rules ?? []).map((rule, index) => (
                <li key={index}>{rule}</li>
              ))}
            </ul>
          </fieldset>
          <button
            type="button"
            className="user-next-button"
            onClick={handleNextClick}
            style={nextButtonTextColor ? { color: nextButtonTextColor } : undefined}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

export default UserRules;